// =============================================
// COMPONENTE: NODE ACTIONS EDITOR
// =============================================
// Arquivo: NodeActionsEditor.tsx
// Descrição: Editor de ações de entrada/saída dos nodes
// =============================================

import React, { useState } from 'react';
import {
  Plus, Trash2, Settings,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import {
  ActionType,
  InputAction,
  OutputAction,
  NodeActions,
  ACTION_DEFINITIONS,
  getActionDefinition,
  createNewAction
} from './types/actions';
import './NodeActionsEditor.css';

interface NodeActionsEditorProps {
  actions: NodeActions;
  onChange: (actions: NodeActions) => void;
}

const NodeActionsEditor: React.FC<NodeActionsEditorProps> = ({ actions, onChange }) => {
  const [activeTab, setActiveTab] = useState<'input' | 'output'>('input');
  const [expandedAction, setExpandedAction] = useState<string | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Normalizar actions para garantir estrutura válida
  const normalizedActions: NodeActions = {
    input: Array.isArray(actions?.input) ? actions.input.filter(a => a && a.id && a.type).map(a => ({
      ...a,
      config: a.config || {},
      enabled: a.enabled !== undefined ? a.enabled : true,
      label: a.label || a.type || 'Ação',
      order: a.order ?? 0
    })) : [],
    output: Array.isArray(actions?.output) ? actions.output.filter(a => a && a.id && a.type).map(a => ({
      ...a,
      config: a.config || {},
      enabled: a.enabled !== undefined ? a.enabled : true,
      label: a.label || a.type || 'Ação',
      order: a.order ?? 0
    })) : []
  };

  // Ações por categoria
  const actionsByCategory = ACTION_DEFINITIONS.reduce((acc, def) => {
    if (!acc[def.category]) {
      acc[def.category] = [];
    }
    acc[def.category].push(def);
    return acc;
  }, {} as Record<string, typeof ACTION_DEFINITIONS>);

  const categoryLabels: Record<string, string> = {
    validation: 'Validações',
    transformation: 'Transformações',
    storage: 'Armazenamento',
    control: 'Controle',
    integration: 'Integração',
    data: 'Dados',
    database: 'Banco de Dados',
    api: 'API',
    notification: 'Notificações',
    security: 'Segurança'
  };

  const categoryIcons: Record<string, string> = {
    validation: 'fa-check-circle',
    transformation: 'fa-magic',
    storage: 'fa-database',
    control: 'fa-sliders-h',
    integration: 'fa-plug',
    data: 'fa-table',
    database: 'fa-server',
    api: 'fa-cloud',
    notification: 'fa-bell',
    security: 'fa-shield-alt'
  };

  // Adicionar nova ação
  const handleAddAction = (type: ActionType) => {
    const isInput = activeTab === 'input';
    const newAction = createNewAction(type, isInput);

    // Deep copy das listas existentes
    const inputList = normalizedActions.input.map(a => ({ ...a, config: { ...a.config } }));
    const outputList = normalizedActions.output.map(a => ({ ...a, config: { ...a.config } }));

    if (isInput) {
      inputList.push(newAction as InputAction);
    } else {
      outputList.push(newAction as OutputAction);
    }

    onChange({ input: inputList, output: outputList });
    setShowAddMenu(false);
    setExpandedAction(newAction.id);
  };

  // Remover ação
  const handleRemoveAction = (actionId: string) => {
    // Deep copy das listas
    const inputList = normalizedActions.input
      .filter(a => !(activeTab === 'input' && a.id === actionId))
      .map(a => ({ ...a, config: { ...a.config } }));
    const outputList = normalizedActions.output
      .filter(a => !(activeTab === 'output' && a.id === actionId))
      .map(a => ({ ...a, config: { ...a.config } }));

    onChange({ input: inputList, output: outputList });
  };

  // Mover ação para cima
  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    // Deep copy das listas
    const inputList = normalizedActions.input.map(a => ({ ...a, config: { ...a.config } }));
    const outputList = normalizedActions.output.map(a => ({ ...a, config: { ...a.config } }));

    if (activeTab === 'input') {
      [inputList[index - 1], inputList[index]] = [inputList[index], inputList[index - 1]];
      inputList.forEach((action, idx) => action.order = idx);
    } else {
      [outputList[index - 1], outputList[index]] = [outputList[index], outputList[index - 1]];
      outputList.forEach((action, idx) => action.order = idx);
    }

    onChange({ input: inputList, output: outputList });
  };

  // Mover ação para baixo
  const handleMoveDown = (index: number) => {
    const list = activeTab === 'input' ? normalizedActions.input : normalizedActions.output;
    if (index === list.length - 1) return;

    // Deep copy das listas
    const inputList = normalizedActions.input.map(a => ({ ...a, config: { ...a.config } }));
    const outputList = normalizedActions.output.map(a => ({ ...a, config: { ...a.config } }));

    if (activeTab === 'input') {
      [inputList[index], inputList[index + 1]] = [inputList[index + 1], inputList[index]];
      inputList.forEach((action, idx) => action.order = idx);
    } else {
      [outputList[index], outputList[index + 1]] = [outputList[index + 1], outputList[index]];
      outputList.forEach((action, idx) => action.order = idx);
    }

    onChange({ input: inputList, output: outputList });
  };

  // Atualizar configuração de ação
  const handleUpdateConfig = (actionId: string, key: string, value: any) => {
    // Deep copy para garantir que o React detecte a mudança
    const updatedActions = {
      input: normalizedActions.input.map(a => a.id === actionId && activeTab === 'input'
        ? { ...a, config: { ...a.config, [key]: value } }
        : { ...a, config: { ...a.config } }
      ),
      output: normalizedActions.output.map(a => a.id === actionId && activeTab === 'output'
        ? { ...a, config: { ...a.config, [key]: value } }
        : { ...a, config: { ...a.config } }
      )
    };
    onChange(updatedActions);
  };

  // Toggle enabled
  const handleToggleEnabled = (actionId: string) => {
    // Deep copy para garantir que o React detecte a mudança
    const updatedActions = {
      input: normalizedActions.input.map(a => a.id === actionId && activeTab === 'input'
        ? { ...a, config: { ...a.config }, enabled: !a.enabled }
        : { ...a, config: { ...a.config } }
      ),
      output: normalizedActions.output.map(a => a.id === actionId && activeTab === 'output'
        ? { ...a, config: { ...a.config }, enabled: !a.enabled }
        : { ...a, config: { ...a.config } }
      )
    };
    onChange(updatedActions);
  };

  const currentActions = activeTab === 'input' ? normalizedActions.input : normalizedActions.output;

  return (
    <div className="node-actions-editor">
      {/* Tabs */}
      <div className="actions-tabs">
        <button
          className={`actions-tab ${activeTab === 'input' ? 'active' : ''}`}
          onClick={() => setActiveTab('input')}
        >
          <i className="fas fa-sign-in-alt"></i>
          <span>Entrada ({normalizedActions.input.length})</span>
        </button>
        <button
          className={`actions-tab ${activeTab === 'output' ? 'active' : ''}`}
          onClick={() => setActiveTab('output')}
        >
          <i className="fas fa-sign-out-alt"></i>
          <span>Saída ({normalizedActions.output.length})</span>
        </button>
      </div>

      {/* Info */}
      <div className="actions-info">
        <AlertCircle size={14} />
        <p>
          {activeTab === 'input'
            ? 'Ações executadas ANTES do processamento do node'
            : 'Ações executadas APÓS o processamento do node'}
        </p>
      </div>

      {/* Lista de Ações */}
      <div className="actions-list">
        {currentActions.length === 0 ? (
          <div className="actions-empty">
            <Settings size={32} />
            <p>Nenhuma ação configurada</p>
            <span>Clique em "Adicionar Ação" para começar</span>
          </div>
        ) : (
          currentActions.map((action, index) => {
            const definition = getActionDefinition(action.type);
            if (!definition) return null;

            const isExpanded = expandedAction === action.id;

            return (
              <div
                key={action.id}
                className={`action-item ${!action.enabled ? 'disabled' : ''} ${isExpanded ? 'expanded' : ''}`}
              >
                {/* Header */}
                <div className="action-header">
                  <div className="action-header-left">
                    <button
                      className="action-toggle-btn"
                      onClick={() => handleToggleEnabled(action.id)}
                      title={action.enabled ? 'Desabilitar' : 'Habilitar'}
                    >
                      {action.enabled ? (
                        <CheckCircle size={16} className="icon-enabled" />
                      ) : (
                        <XCircle size={16} className="icon-disabled" />
                      )}
                    </button>
                    <i className={`fas ${definition.icon} action-icon`}></i>
                    <span className="action-label">{action.label}</span>
                    <span className="action-order">#{index + 1}</span>
                  </div>
                  <div className="action-header-right">
                    <button
                      className="action-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Mover para cima"
                    >
                      <i className="fas fa-chevron-up" style={{ fontSize: '14px' }}></i>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === currentActions.length - 1}
                      title="Mover para baixo"
                    >
                      <i className="fas fa-chevron-down" style={{ fontSize: '14px' }}></i>
                    </button>
                    <button
                      className="action-btn action-btn-expand"
                      onClick={() => setExpandedAction(isExpanded ? null : action.id)}
                      title={isExpanded ? 'Recolher' : 'Expandir'}
                    >
                      <Settings size={14} />
                    </button>
                    <button
                      className="action-btn action-btn-danger"
                      onClick={() => handleRemoveAction(action.id)}
                      title="Remover"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Config (quando expandido) */}
                {isExpanded && (
                  <div className="action-config">
                    <p className="action-description">{definition.description}</p>
                    {definition.configSchema.map((field) => (
                      <div key={field.key} className="config-field">
                        <label>
                          {field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        {field.type === 'text' && (
                          <input
                            type="text"
                            value={action.config[field.key] || ''}
                            onChange={(e) => handleUpdateConfig(action.id, field.key, e.target.value)}
                            placeholder={field.placeholder}
                          />
                        )}
                        {field.type === 'number' && (
                          <input
                            type="number"
                            value={action.config[field.key] || ''}
                            onChange={(e) => handleUpdateConfig(action.id, field.key, e.target.value)}
                            placeholder={field.placeholder}
                          />
                        )}
                        {field.type === 'textarea' && (
                          <textarea
                            value={action.config[field.key] || ''}
                            onChange={(e) => handleUpdateConfig(action.id, field.key, e.target.value)}
                            placeholder={field.placeholder}
                            rows={4}
                          />
                        )}
                        {field.type === 'select' && (
                          <select
                            value={action.config[field.key] || ''}
                            onChange={(e) => handleUpdateConfig(action.id, field.key, e.target.value)}
                          >
                            <option value="">Selecione...</option>
                            {field.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {field.type === 'checkbox' && (
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={action.config[field.key] || false}
                              onChange={(e) => handleUpdateConfig(action.id, field.key, e.target.checked)}
                            />
                            <span>Habilitado</span>
                          </label>
                        )}
                        {field.type === 'array' && (
                          <input
                            type="text"
                            value={Array.isArray(action.config[field.key]) ? action.config[field.key].join(', ') : ''}
                            onChange={(e) => {
                              const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                              handleUpdateConfig(action.id, field.key, values);
                            }}
                            placeholder={field.placeholder}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Botão Adicionar */}
      <div className="actions-add-section">
        {!showAddMenu ? (
          <button
            className="btn-add-action"
            onClick={() => setShowAddMenu(true)}
          >
            <Plus size={16} />
            <span>Adicionar Ação</span>
          </button>
        ) : (
          <div className="add-action-menu">
            <div className="add-action-menu-header">
              <h4>Selecione o Tipo de Ação</h4>
              <button
                className="btn-close-menu"
                onClick={() => setShowAddMenu(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="add-action-categories">
              {Object.entries(actionsByCategory).map(([category, defs]) => (
                <div key={category} className="action-category">
                  <div className="category-header">
                    <i className={`fas ${categoryIcons[category]}`}></i>
                    <span>{categoryLabels[category]}</span>
                  </div>
                  <div className="category-actions">
                    {defs.map((def) => (
                      <button
                        key={def.type}
                        className="action-option"
                        onClick={() => handleAddAction(def.type)}
                      >
                        <i className={`fas ${def.icon}`}></i>
                        <div className="action-option-info">
                          <span className="action-option-label">{def.label}</span>
                          <span className="action-option-desc">{def.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeActionsEditor;
